import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  PrimaryButton,
  DefaultButton,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
} from "@fluentui/react";
import { Flex, HSpace, NavigationBar, Right } from "../../../components/styled";
import { NavButtons } from "../../../components/navigation/NavButtons";
import { useSelector } from "../../../store/hooks";
import { AppDispatch } from "../../../store/store";
import {
  clearModifiedRows,
  saveEditableRecommendationsActions,
} from "../../../store/recommendationsEditable/recommendationsEditable";
import { loadRecommendationsActions } from "../../../store/recommendations/recommendations";
import { routes } from "../../../config/routes";
import { RefreshButton } from "./RefreshButton";

type Props = {
  hasModifications: boolean;
  selectedCount: number;
  totalCount: number;
  onBulkUpdate: () => void;
};

export const Navigation = ({
  hasModifications,
  selectedCount,
  totalCount,
  onBulkUpdate,
}: Props) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const isSaving = useSelector((s) => s.recommendationsEditable.isSaving);
  const modifiedRows = useSelector(
    (s) => s.recommendationsEditable.modifiedRows,
  );
  const count = useSelector((s) => s.recommendationsEditable.count);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [wasSaving, setWasSaving] = useState(false);

  // Watch for save completion to navigate
  useEffect(() => {
    if (wasSaving && !isSaving) {
      setWasSaving(false);
      dispatch(clearModifiedRows());
      navigate(routes.recommendations.getPath());
      dispatch(
        loadRecommendationsActions.trigger({
          skip: 0,
          top: 100,
          reset: true,
        }),
      );
    }
  }, [isSaving, wasSaving, dispatch, navigate]);

  const handleSave = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmSave = () => {
    setIsConfirmDialogOpen(false);
    const recommendations = Object.values(modifiedRows);
    if (recommendations.length > 0) {
      setWasSaving(true);
      dispatch(saveEditableRecommendationsActions.trigger({ recommendations }));
    }
  };

  const handleCancelConfirm = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    dispatch(clearModifiedRows());
    navigate(routes.recommendations.getPath());
  };

  const handleBack = () => {
    if (hasModifications) {
      const confirmed = window.confirm(
        t("ram.recommendationsEditable.unsavedChangesConfirm"),
      );
      if (!confirmed) return;
    }
    dispatch(clearModifiedRows());
    navigate(routes.recommendations.getPath());
  };

  const modifiedCount = Object.keys(modifiedRows).length;

  return (
    <NavigationBar>
      <NavButtons />
      <HSpace width={10} />
      <DefaultButton
        iconProps={{ iconName: "Back" }}
        text={t("ram.recommendationsEditable.back")}
        onClick={handleBack}
      />
      <HSpace width={10} />
      {/* <RefreshButton /> */}
      <Right>
        <Flex>
          {selectedCount > 0 && (
            <>
              <DefaultButton
                iconProps={{ iconName: "BulkUpload" }}
                text={t("ram.recommendationsEditable.bulkUpdate.button")}
                onClick={onBulkUpdate}
              />
              <HSpace width={10} />
            </>
          )}
          {hasModifications && (
            <>
              <span style={{ alignSelf: "center", marginRight: "10px" }}>
                {t("ram.recommendationsEditable.modifiedCount", {
                  count: modifiedCount,
                })}
              </span>
              <DefaultButton
                text={t("ram.recommendationsEditable.cancel")}
                onClick={handleCancel}
                disabled={isSaving}
              />
              <HSpace width={10} />
              <PrimaryButton
                text={t("ram.recommendationsEditable.save")}
                onClick={handleSave}
                disabled={isSaving || !hasModifications}
              >
                {isSaving && (
                  <>
                    <HSpace width={5} />
                    <Spinner size={SpinnerSize.xSmall} />
                  </>
                )}
              </PrimaryButton>
            </>
          )}
          <span
            style={{
              alignSelf: "center",
              marginRight: "10px",
              marginLeft: "10px",
              fontSize: "14px",
            }}
          >
            {t("ram.recommendationsEditable.selectedRecommendations", {
              selected: selectedCount,
              total: totalCount,
            })}
          </span>
          <HSpace width={10} />
        </Flex>
      </Right>
      <Dialog
        hidden={!isConfirmDialogOpen}
        onDismiss={handleCancelConfirm}
        dialogContentProps={{
          type: DialogType.normal,
          title: t("ram.recommendationsEditable.confirmSave.title"),
          subText: t("ram.recommendationsEditable.confirmSave.description", {
            count: modifiedCount,
          }),
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        <p>{t("ram.recommendationsEditable.confirmSave.adjustmentWarning")}</p>
        <p>&nbsp;</p>
        <p>{t("ram.recommendationsEditable.confirmSave.delayWarning")}</p>
        <DialogFooter>
          <PrimaryButton
            onClick={handleConfirmSave}
            text={t("ram.recommendationsEditable.confirmSave.confirm")}
          />
          <DefaultButton
            onClick={handleCancelConfirm}
            text={t("greco.cancel")}
          />
        </DialogFooter>
      </Dialog>
    </NavigationBar>
  );
};
